import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Chapter } from "@/components/Chapters";
import { useLang } from "@/i18n";

const SORO_SRC = "https://app.trysoro.com/api/embed/38adbf6a-51e4-40a0-a4c6-69e678e73bd8";

const reveal = {
  hidden: { opacity: 0, y: 48 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Blog() {
  const { t } = useLang();
  const hostRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // The vendor script targets this exact id — it must exist before the script loads.
    const target = document.createElement("div");
    target.id = "soro-blog";
    host.appendChild(target);

    const script = document.createElement("script");
    script.src = SORO_SRC;
    script.defer = true;
    script.onload = () => setError(false);
    script.onerror = () => setError(true);
    target.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
      host.replaceChildren();
    };
  }, []);

  return (
    <Chapter id="blog" number="09" eyebrow={t.blog.eyebrow} testid="chapter-blog">
      <motion.h2
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-15%" }}
        className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
      >
        {t.blog.h2a} <span className="italic text-copper">{t.blog.h2b}</span>
      </motion.h2>
      <motion.p
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-15%" }}
        className="mt-8 max-w-xl text-base leading-relaxed text-bone/60"
      >
        {t.blog.intro}
      </motion.p>

      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-10%" }}
        className="mt-14 border-t border-white/10 pt-14"
        data-testid="blog-embed"
      >
        <div ref={hostRef} className="min-h-[120px]" />
        {error && (
          <p data-testid="blog-load-error" className="text-sm text-copper">
            {t.blog.loadError}
          </p>
        )}
      </motion.div>
    </Chapter>
  );
}
